import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useParams } from 'react-router-dom';
import { MenuItem, MenuItemDetail } from '../../../services/restaurantService';
import { getVariantUnitPrice, hasFlatPromotion } from '../../../utils/menuPricing';
import {
  getAddonsAmount,
  itemHasOptions,
  lineTotal,
  validateSelections,
} from '../../../utils/menuOptions';
import { useGuestNoteSettings } from '../../../hooks/useGuestNoteSettings';
import MenuOrderOptions, { MenuOrderSelections } from '../MenuOrderOptions/MenuOrderOptions';
import './MenuItemOptionsSheet.css';

interface MenuItemOptionsSheetProps {
  isOpen: boolean;
  item: MenuItem | MenuItemDetail;
  selections: MenuOrderSelections;
  onChange: (selections: MenuOrderSelections) => void;
  onClose: () => void;
  /** Called with the quantity chosen in the sheet once the selection is valid. */
  onConfirm: (quantity: number) => void;
  formatPrice: (price: number) => string;
  error?: string | null;
  confirmLabel?: string;
  /** Defaults to the :appName route param. */
  appName?: string;
  /** Quantity the stepper starts at (editing a cart line keeps its quantity). */
  initialQuantity?: number;
  busy?: boolean;
}

const MAX_SHEET_QTY = 50;

const MenuItemOptionsSheet: React.FC<MenuItemOptionsSheetProps> = ({
  isOpen,
  item,
  selections,
  onChange,
  onClose,
  onConfirm,
  formatPrice,
  error,
  confirmLabel = 'Add to cart',
  appName,
  initialQuantity = 1,
  busy = false,
}) => {
  const params = useParams<{ appName: string }>();
  const noteSettings = useGuestNoteSettings(appName || params.appName, isOpen);
  const [quantity, setQuantity] = useState(initialQuantity);
  const [localError, setLocalError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      setQuantity(Math.max(1, initialQuantity));
      setLocalError(null);
    }
  }, [isOpen, initialQuantity, item.id]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [isOpen, onClose]);

  if (!isOpen) {
    return null;
  }

  // Unit = variant + add-ons (rounded once), then times quantity. A flat promotion comes off the
  // bill once at checkout, so it is shown as a hint rather than taken off each unit.
  const total = lineTotal(
    getVariantUnitPrice(item, { sizeTypeId: selections.sizeTypeId }),
    getAddonsAmount(item, selections),
    quantity
  );
  const flatOffer = hasFlatPromotion(item) && item.promotionBadge ? item.promotionBadge : null;
  const shownError = localError || error;

  const handleChange = (next: MenuOrderSelections) => {
    setLocalError(null);
    onChange(next);
  };

  const handleConfirm = () => {
    const err = validateSelections(item, selections, noteSettings.itemNoteMaxLength);
    if (err) {
      setLocalError(err);
      return;
    }
    setLocalError(null);
    onConfirm(quantity);
  };

  return createPortal(
    <div className="menu-options-sheet-overlay" onClick={onClose} role="presentation">
      <div
        className="menu-options-sheet-panel"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="menu-options-sheet-title"
      >
        <div className="menu-options-sheet-handle" aria-hidden="true" />
        <header className="menu-options-sheet-header">
          <div className="menu-options-sheet-header-text">
            <h3 id="menu-options-sheet-title">{item.item}</h3>
            <p className="menu-options-sheet-subtitle">
              {itemHasOptions(item) ? 'Choose options before adding' : 'Add a cooking request'}
            </p>
          </div>
          <button type="button" className="menu-options-sheet-close" onClick={onClose} aria-label="Close">
            <i className="fa fa-times" aria-hidden="true" />
          </button>
        </header>

        <div className="menu-options-sheet-body">
          <MenuOrderOptions
            item={item}
            selections={selections}
            onChange={handleChange}
            formatPrice={formatPrice}
            noteSettings={noteSettings}
          />
          {flatOffer && (
            <p className="menu-options-sheet-offer">
              <span className="menu-options-sheet-offer__badge">{flatOffer}</span> applied to your bill
              at checkout
            </p>
          )}
          {shownError && (
            <p className="menu-options-sheet-error" role="alert">
              {shownError}
            </p>
          )}
        </div>

        <footer className="menu-options-sheet-footer">
          <div className="menu-options-sheet-qty" role="group" aria-label="Quantity">
            <button
              type="button"
              className="menu-options-sheet-qty-btn"
              onClick={() => setQuantity((q) => Math.max(1, q - 1))}
              disabled={quantity <= 1}
              aria-label="Decrease quantity"
            >
              −
            </button>
            <span className="menu-options-sheet-qty-val" aria-live="polite">
              {quantity}
            </span>
            <button
              type="button"
              className="menu-options-sheet-qty-btn"
              onClick={() => setQuantity((q) => Math.min(MAX_SHEET_QTY, q + 1))}
              disabled={quantity >= MAX_SHEET_QTY}
              aria-label="Increase quantity"
            >
              +
            </button>
          </div>
          <button
            type="button"
            className="menu-options-sheet-btn menu-options-sheet-btn--primary"
            onClick={handleConfirm}
            disabled={busy}
            aria-busy={busy}
          >
            {confirmLabel}
            <span className="menu-options-sheet-btn-price">{formatPrice(total)}</span>
          </button>
        </footer>
      </div>
    </div>,
    document.body
  );
};

export default MenuItemOptionsSheet;
